import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
    CAlert,
    CBadge,
    CButton,
    CCard,
    CCardBody,
    CCardHeader,
    CProgress,
    CSpinner
} from '@coreui/react';
import { getApi } from '../../apis/backendApis';
import {
    InfraPipelineLinha,
    InfraProducaoResumo,
    TIPO_PRODUCAO,
    corSituacaoPipeline,
    descricaoSituacaoPipeline,
    rotuloDeploy
} from '../../models/InfraPipeline';
import '../../scss/styles-infra.scss';

const PipelineList = () => {
    const history = useHistory();
    const [linhas, setLinhas] = useState<Array<InfraPipelineLinha>>([]);
    const [producao, setProducao] = useState<InfraProducaoResumo | null>(null);
    const [carregando, setCarregando] = useState<boolean>(true);
    const [abrindo, setAbrindo] = useState<boolean>(false);
    const [erro, setErro] = useState<string | null>(null);

    const carregar = useCallback(() => {
        setCarregando(true);
        Promise.all([
            getApi().get('/infra/pipeline/list'),
            getApi().get('/infra/producao/resumo')
        ])
            .then(([lista, prod]) => {
                setLinhas(lista.data.rows || []);
                setProducao(prod.data || null);
                setErro(null);
            })
            .catch(motivo => setErro(motivo?.response?.data?.error || 'Não consegui carregar os pipelines.'))
            .finally(() => setCarregando(false));
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    /**
     * Abre o deploy de producao atual.
     *
     * Vai direto para o que existe, aberto ou encerrado, sem criar nada: o
     * clique aqui e navegacao, e comecar um deploy e decisao, que se toma
     * dentro da tela do deploy com o fluxo a vista. So quando nunca rodou nada
     * e que este clique abre o primeiro, porque ai nao ha para onde navegar.
     */
    const abrirProducao = () => {
        if (abrindo) {
            return;
        }

        if (producao?.pipeline) {
            history.push('/infra/pipeline/' + producao.pipeline.id);
            return;
        }

        setAbrindo(true);
        getApi().post('/infra/producao/abrir', {})
            .then(resposta => history.push('/infra/pipeline/' + resposta.data.id))
            .catch(motivo => {
                setErro(motivo?.response?.data?.error || 'Não consegui abrir o deploy de produção.');
                setAbrindo(false);
            });
    };

    const sandboxes = linhas.filter(linha => linha.tipo !== TIPO_PRODUCAO);

    const emAndamento = producao?.pipeline || null;
    const percentualProducao = emAndamento && emAndamento.total > 0
        ? Math.round((emAndamento.concluidos / emAndamento.total) * 100)
        : 0;

    return (
        <div className="infra-container infra-container-rolavel">
            <div className="infra-cabecalho">
                <div>
                    <h4 className="infra-titulo">Central de Infra</h4>
                    <div className="infra-subtitulo">
                        <span>Deploy da frota e provisionamento de clientes sandbox</span>
                    </div>
                </div>
                <div className="infra-acoes-topo">
                    <CButton color="light" onClick={carregar} disabled={carregando}>Atualizar</CButton>
                    <CButton color="light" onClick={() => history.push('/infra/loja/nova')}>
                        Nova loja
                    </CButton>
                    <CButton color="primary" onClick={() => history.push('/infra/novo')}>
                        Novo sandbox
                    </CButton>
                </div>
            </div>

            {erro && <CAlert color="danger">{erro}</CAlert>}

            {carregando && linhas.length === 0 && !producao && (
                <div className="infra-carregando"><CSpinner color="primary" /></div>
            )}

            {producao && (
                // Um clique, um destino: abre o deploy atual.
                //
                // As ações de deploy (continuar, começar do zero) e o histórico
                // vivem dentro da tela do deploy, que é onde se decide isso com
                // o fluxo à vista. Aqui só se entra.
                <CCard className="infra-card-producao" onClick={abrirProducao}>
                    <CCardHeader className="infra-card-header">
                        <div>
                            <strong>Ambiente Produção</strong>
                            <span className="infra-card-slug">
                                Template &rarr; AMI &rarr; Farm1, Pano, Farm2 e Virginia
                            </span>
                        </div>
                        {emAndamento
                            ? (
                                <CBadge color={corSituacaoPipeline(emAndamento.situacao)}>
                                    {descricaoSituacaoPipeline(emAndamento.situacao)}
                                </CBadge>
                            )
                            : <CBadge color="secondary">Nunca rodou</CBadge>
                        }
                    </CCardHeader>
                    <CCardBody>
                        {emAndamento && (
                            <>
                                <CProgress
                                    value={percentualProducao}
                                    color={emAndamento.erros > 0 ? 'danger' : 'success'}
                                    className="mb-2"
                                    style={{ height: 8 }}
                                />
                                <div className="infra-card-info">
                                    <span>{emAndamento.concluidos} de {emAndamento.total} passos</span>
                                    <span>{rotuloDeploy(emAndamento.id)}</span>
                                </div>
                            </>
                        )}

                    </CCardBody>
                </CCard>
            )}

            <hr className="infra-separador" />

            <div className="infra-secao-titulo">Sandboxes</div>

            {!carregando && sandboxes.length === 0 && (
                <CAlert color="secondary">
                    Nenhum sandbox ainda. Comece criando um.
                </CAlert>
            )}

            <div className="infra-lista">
                {sandboxes.map(linha => {
                    const percentual = linha.total > 0
                        ? Math.round((linha.concluidos / linha.total) * 100)
                        : 0;

                    return (
                        <CCard
                            key={linha.id}
                            className="infra-card-pipeline"
                            onClick={() => history.push('/infra/pipeline/' + linha.id)}
                        >
                            <CCardHeader className="infra-card-header">
                                <div>
                                    <strong>{linha.nome}</strong>
                                    <span className="infra-card-slug">{linha.slug}</span>
                                </div>
                                <CBadge color={corSituacaoPipeline(linha.situacao)}>
                                    {descricaoSituacaoPipeline(linha.situacao)}
                                </CBadge>
                            </CCardHeader>
                            <CCardBody>
                                <CProgress
                                    value={percentual}
                                    color={linha.erros > 0 ? 'danger' : 'success'}
                                    className="mb-2"
                                    style={{ height: 8 }}
                                />
                                <div className="infra-card-info">
                                    <span>{linha.concluidos} de {linha.total} passos</span>
                                    <span>{linha.farm}</span>
                                </div>
                                {linha.sandbox && (
                                    <div className="infra-card-sandbox">{linha.sandbox}</div>
                                )}
                                <div className="infra-card-rodape">
                                    Atualizado em {linha.atualizado}
                                </div>
                            </CCardBody>
                        </CCard>
                    );
                })}
            </div>
        </div>
    );
};

export default PipelineList;
